/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Typography2 } from '@coursera/cds-core';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import { CERTIFICATE_TYPE_TO_SLUG } from 'bundles/premium-certificate/constants';

import _t from 'i18n!nls/premium-certificate';

type Props = {
  certificateName?: Maybe<string>;
  certificateType?: Maybe<string>;
};

const styles: Record<string, Interpolation<Theme>> = {
  certificateBreadcrumbs: css`
    margin-bottom: 3.5rem;
  `,
  breadcrumbList: css`
    list-style-type: none;
    padding: 0;
    margin: 0;
  `,
  breadcrumbItem: css`
    display: inline;
  `,
  breadcrumbLink: () => css`
    display: inline;

    a {
      color: var(--cds-color-grey-975);
      font-size: 0.875rem;
      line-height: 1.5rem;
    }

    text-decoration: none;

    :hover {
      text-decoration: none;
      color: var(--cds-color-grey-975);
    }
  `,
  breadcrumbSeparator: css`
    margin: 0 0.5rem;
    font-weight: 400;
  `,
};

const CertificateBreadcrumbs = ({ certificateName, certificateType }: Props) => {
  if (!certificateType) {
    return null;
  }

  const certificateHubLink = `/${CERTIFICATE_TYPE_TO_SLUG[certificateType]}`;
  const breadcrumbLinks = [
    {
      href: '/certificates',
      label: _t('Certificates'),
    },
    {
      href: certificateHubLink,
      label: certificateType,
    },
    {
      href: undefined,
      label: certificateName,
    },
  ];

  return (
    <div
      data-test="CertificateBreadcrumbs"
      css={styles.certificateBreadcrumbs}
      role="navigation"
      aria-label="Breadcrumb"
    >
      <ol css={styles.breadcrumbList}>
        {breadcrumbLinks.map(({ href, label }) => (
          <li key={`breadcrumb-${label}`} css={styles.breadcrumbItem}>
            {href ? (
              <strong css={styles.breadcrumbLink}>
                <TrackedLink2 href={href} trackingName="breadcrumb_link">
                  {label}
                </TrackedLink2>
                <span css={styles.breadcrumbSeparator} aria-hidden="true">
                  &gt;
                </span>
              </strong>
            ) : (
              <Typography2 component="p" variant="bodyPrimary" css={styles.breadcrumbLink} aria-current="page">
                {label}
              </Typography2>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default CertificateBreadcrumbs;
